// Kron — combined app JS (kron-app.js)
// Bundled from kron-variant-swap.js + kron-bundle-compare.js + kron-variant-stock-v8.js
// Each module is self-contained IIFE — no global scope conflicts.
// Built: 2026-05-25 — Phase 3 JS consolidation.


/* ===== assets/kron-variant-swap.js ===== */
// Kron — variant swap behavior (v4, robust media-id resolution — 2026-05-22)
//
// COMPORTAMENTO:
//   1. Foto da variante selecionada SEMPRE vai pra primeira posição do grid (CSS order).
//      A foto que estava em primeiro vai pra posição da foto da variante (swap).
//   2. No MOBILE (carousel scrollable), além do reorder, scrolla suavemente
//      pra primeira posição (left: 0).
//   3. No DESKTOP (grid não scrollable), o reorder via `order` é suficiente.
//
// v4 (2026-05-22):
//   - Resolução robusta do media-id alvo: tenta featured_media.id, depois
//     featured_image (lookup em window.__kronProductData), depois match por
//     filename. Antes só usava featured_media.id e silenciava quando ausente.

(function () {
  // ─── 1. Hijack scrollIntoView dentro do product-gallery ────────────────
  const _origScrollIntoView = Element.prototype.scrollIntoView;
  Element.prototype.scrollIntoView = function (...args) {
    if (this.matches?.('[data-media-id]') && this.closest?.('.product-gallery, [class*="product-gallery"]')) {
      return;
    }
    return _origScrollIntoView.apply(this, args);
  };

  // ─── 2. Hijack scroll-carousel.select para bloquear scroll-to-featured ─
  let blockNextSelect = false;
  function tryPatchCarousel() {
    const Ctor = window.customElements?.get?.('scroll-carousel');
    if (!Ctor?.prototype || typeof Ctor.prototype.select !== 'function') return false;
    if (Ctor.prototype.__kronPatched) return true;
    const original = Ctor.prototype.select;
    Ctor.prototype.select = function (index, options = {}) {
      if (blockNextSelect && this.classList.contains('product-gallery__carousel')) {
        return;
      }
      return original.call(this, index, options);
    };
    Ctor.prototype.__kronPatched = true;
    return true;
  }
  if (!tryPatchCarousel()) {
    window.customElements?.whenDefined?.('scroll-carousel').then(tryPatchCarousel);
  }

  // ─── Helpers ────────────────────────────────────────────────────────────
  function stripQuery(url) {
    return typeof url === 'string' ? url.split('?')[0] : '';
  }

  function basenameNoVersion(url) {
    const stripped = stripQuery(url);
    return stripped.split('/').pop() || '';
  }

  // Resolve media-id do variant tentando múltiplas fontes
  function resolveVariantMediaId(variant, gallery) {
    if (!variant) return null;

    // 1) featured_media.id (novo media API)
    if (variant.featured_media?.id) return String(variant.featured_media.id);

    // 2) Lookup em window.__kronProductData via src
    const featuredSrc = variant.featured_image?.src || (typeof variant.featured_image === 'string' ? variant.featured_image : '');
    const productData = window.__kronProductData;
    if (featuredSrc && productData?.media && Array.isArray(productData.media)) {
      const target = stripQuery(featuredSrc);
      const found = productData.media.find(m => {
        const cands = [m.src, m.preview_image?.src, m.preview?.image?.src].filter(Boolean);
        return cands.some(c => stripQuery(c) === target);
      });
      if (found?.id) return String(found.id);
    }

    // 3) Match por basename na própria galeria (sem depender de productData)
    if (featuredSrc && gallery) {
      const targetBase = basenameNoVersion(featuredSrc);
      const slides = Array.from(gallery.querySelectorAll('[data-media-id]'));
      for (const slide of slides) {
        const img = slide.querySelector('img');
        if (!img) continue;
        const srcs = [img.currentSrc, img.src, ...(img.srcset || '').split(',').map(s => s.trim().split(' ')[0])].filter(Boolean);
        for (const s of srcs) {
          if (basenameNoVersion(s) === targetBase) {
            return slide.dataset.mediaId;
          }
        }
      }
    }

    // 4) featured_image.id (last resort — pode bater se data-media-id usa ProductImage IDs)
    if (variant.featured_image?.id) return String(variant.featured_image.id);

    return null;
  }

  // ─── 3. Reorder + mobile scroll ─────────────────────────────────────────
  function setupGallery(gallery) {
    if (!gallery || gallery.dataset.kronVariantSwap === '1') return;
    gallery.dataset.kronVariantSwap = '1';

    const slides = Array.from(gallery.querySelectorAll('[data-media-id]'));
    slides.forEach((el, i) => { el.style.order = String(i); });

    const carousel = gallery.querySelector('scroll-carousel.product-gallery__carousel') || gallery.closest('scroll-carousel');

    function swapToFirst(targetMediaId) {
      if (!targetMediaId) return false;
      const target = gallery.querySelector(`[data-media-id="${targetMediaId}"]`);
      if (!target) return false;
      const currentOrder = parseInt(target.style.order || '0', 10);
      if (currentOrder === 0) return false;
      const allSlides = Array.from(gallery.querySelectorAll('[data-media-id]'));
      const first = allSlides.find(el => parseInt(el.style.order || '0', 10) === 0);
      if (first) first.style.order = String(currentOrder);
      target.style.order = '0';
      return true;
    }

    function scrollToFirst() {
      const target = carousel || gallery.querySelector('.product-gallery__carousel');
      if (!target) return;
      if (target.scrollWidth <= target.clientWidth + 4) return;
      target.scrollTo({ left: 0, behavior: 'smooth' });
    }

    function getCurrentVariantMediaId() {
      const params = new URLSearchParams(location.search);
      const variantId = params.get('variant');
      if (variantId && window.__kronProductData) {
        const v = window.__kronProductData.variants?.find(x => String(x.id) === variantId);
        if (v) {
          const resolved = resolveVariantMediaId(v, gallery);
          if (resolved) return resolved;
        }
      }
      const current = gallery.querySelector('[data-media-id][aria-current="true"]');
      if (current) return current.dataset.mediaId;
      return null;
    }

    const initialId = getCurrentVariantMediaId();
    if (initialId) swapToFirst(initialId);

    document.addEventListener('variant:change', (e) => {
      const variant = e?.detail?.variant;
      const mediaId = resolveVariantMediaId(variant, gallery);
      if (!mediaId) return;
      blockNextSelect = true;
      requestAnimationFrame(() => { blockNextSelect = false; });
      const swapped = swapToFirst(mediaId);
      if (swapped) scrollToFirst();
    });
  }

  function init() {
    document.querySelectorAll('product-gallery').forEach(setupGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();



/* ===== assets/kron-bundle-compare.js ===== */
/* ============================================================================
 * Kron — Bundle compare-price + pagination nav (v7)
 * v7: defensivo — NUNCA wipa DOM existente quando dados estão inválidos
 *     (liquid render fica como source of truth). Apenas atualiza quando temos
 *     dados válidos.
 * ========================================================================== */
(function () {
  'use strict';

  var ICON_LEFT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>';
  var ICON_RIGHT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>';

  var BUNDLE_MULT = Object.freeze({ b1g1: 2, b2g2: 4, pair: 2, single: 1 });
  var MUTATION_DEBOUNCE_MS = 120;

  var wiredScroll = new WeakSet();
  var wiredContent = new WeakSet();
  var wiredResize = new WeakSet();
  var navByTrack = new WeakMap();

  var cachedSymbol = null;
  function detectSymbol() {
    if (cachedSymbol !== null) return cachedSymbol;
    try {
      var el = document.querySelector('.bundle-option__price');
      if (el && el.textContent) {
        var m = el.textContent.match(/[^\d.,\s]/);
        if (m) { cachedSymbol = m[0]; return cachedSymbol; }
      }
    } catch (e) {}
    cachedSymbol = '£';
    return cachedSymbol;
  }
  function moneyFormat(cents) {
    return detectSymbol() + (cents / 100).toFixed(2);
  }
  function parsePriceCents(text) {
    if (!text) return 0;
    var n = parseFloat(text.replace(/[^\d.,]/g, '').replace(',', '.'));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  // ============================================================================
  // BUNDLE COMPARE-PRICE (defensive)
  // ============================================================================
  function getSelectedVariant() {
    try {
      var node = document.querySelector('variant-picker [data-variant]');
      if (node && node.textContent) return JSON.parse(node.textContent);
    } catch (e) {}
    return null;
  }
  function compareAtCentsForVariant(v) {
    if (!v || v.compare_at_price == null || v.compare_at_price === '') return 0;
    var cap = v.compare_at_price;
    if (typeof cap === 'number') return cap > 1000 ? cap : Math.round(cap * 100);
    var n = parseFloat(String(cap).replace(',', '.'));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function injectComparePrice(option, variant) {
    var type = option.getAttribute('data-option');
    var mult = BUNDLE_MULT[type] || 1;
    if (mult === 0) return;
    // Kron 2026-05-23: anchor (single sem promo) NÃO recebe compare price injetada
    if (option.classList.contains('bundle-option--anchor')) {
      var existingCp = option.querySelectorAll('.bundle-option__compare-price');
      for (var ci = 0; ci < existingCp.length; ci++) existingCp[ci].remove();
      return;
    }

    var pricesWrap = option.querySelector('.bundle-option__prices');
    var priceEl = option.querySelector('.bundle-option__price');
    var savingsEl = option.querySelector('.bundle-offer__savings');
    if (!pricesWrap || !priceEl) return;

    var compareAt = compareAtCentsForVariant(variant);
    var bundleTotal = parsePriceCents(priceEl.textContent);

    // DEFENSIVE: se faltar qualquer dado válido, NÃO TOQUE no DOM existente.
    // O liquid renderizou compare-price + savings server-side; deixe ele de pé.
    if (compareAt <= 0 || bundleTotal <= 0) return;

    var compareTotalCents = compareAt * mult;
    if (compareTotalCents <= bundleTotal) {
      // Bundle igual/maior que compare — esconde savings + remove compare existente.
      // (Edge case raro, mas válido pra esconder.)
      var stale = pricesWrap.querySelectorAll('.bundle-option__compare-price');
      for (var k = 0; k < stale.length; k++) stale[k].remove();
      if (savingsEl) savingsEl.style.display = 'none';
      return;
    }

    // Dados válidos: dedup + inject fresco
    var existing = pricesWrap.querySelectorAll('.bundle-option__compare-price');
    for (var i = 0; i < existing.length; i++) existing[i].remove();

    var compareDiv = document.createElement('div');
    compareDiv.className = 'bundle-option__compare-price';
    compareDiv.setAttribute('data-kron-compare', '');
    var s = document.createElement('s');
    s.textContent = moneyFormat(compareTotalCents);
    compareDiv.appendChild(s);
    pricesWrap.appendChild(compareDiv);

    if (savingsEl) {
      savingsEl.textContent = 'Save ' + moneyFormat(compareTotalCents - bundleTotal);
      savingsEl.style.display = '';
    }
  }

  function refreshComparePrices() {
    try {
      var variant = getSelectedVariant();
      // Se não conseguiu ler variant, SKIP — não roda inject que poderia wipar DOM
      if (!variant) return;
      var options = document.querySelectorAll('.bundle-option[data-option]');
      for (var i = 0; i < options.length; i++) injectComparePrice(options[i], variant);
    } catch (e) {}
  }

  // ============================================================================
  // PAGINATION NAV (track-agnostic)
  // ============================================================================
  var TRACK_CONFIGS = [
    /* bundle pagination DISABLED here — handled natively by kron-bundle.js renderInlineProducts */
    {
      type: 'tp-pdp',
      trackSelector: '.kron-tpr__track',
      cardSelector: '.kron-tpr__card',
      cardsPerPage: null,
      navClass: 'kron-picker-nav kron-picker-nav--tp',
      findInsertAfter: function (track) {
        return track.closest('.kron-tpr__reviews') || track.parentNode;
      }
    },
    {
      type: 'tp-home',
      trackSelector: '.kron-tp__track',
      cardSelector: '.kron-tp__review',
      cardsPerPage: null,
      navClass: 'kron-picker-nav kron-picker-nav--tp',
      findInsertAfter: function (track) {
        return track.closest('.kron-tp__reviews') || track.parentNode;
      }
    }
  ];

  function countCards(track, cardSelector) {
    var n = track.querySelectorAll(cardSelector).length;
    return n > 0 ? n : track.children.length;
  }

  function getCardsPerPage(track, config) {
    if (config.cardsPerPage) return config.cardsPerPage;
    var cards = track.querySelectorAll(config.cardSelector);
    if (!cards.length) return 1;
    var cardW = cards[0].getBoundingClientRect().width;
    if (cardW < 1) return 1;
    var cs = window.getComputedStyle(track);
    var gap = parseFloat(cs.gap || cs.columnGap || '0') || 0;
    var unit = cardW + gap;
    return Math.max(1, Math.round(track.clientWidth / unit));
  }

  function getPageCount(track, config) {
    var total = countCards(track, config.cardSelector);
    var per = getCardsPerPage(track, config);
    return Math.max(1, Math.ceil(total / per));
  }

  function getCurrentPage(track) {
    var w = track.clientWidth;
    if (w < 1) return 1;
    return Math.round(track.scrollLeft / w) + 1;
  }

  function updateNav(nav, track, config) {
    if (!nav || !track) return;
    var total = getPageCount(track, config);
    var current = getCurrentPage(track);
    if (current > total) current = total;
    if (current < 1) current = 1;
    var counter = nav.querySelector('[data-kron-picker-counter]');
    var prev = nav.querySelector('[data-kron-picker-prev]');
    var next = nav.querySelector('[data-kron-picker-next]');
    if (counter) counter.textContent = current + ' / ' + total;
    if (prev) prev.disabled = current <= 1;
    if (next) next.disabled = current >= total;
    nav.setAttribute('data-kron-has-pages', total > 1 ? 'true' : 'false');
  }

  function scrollByPage(track, direction) {
    try {
      track.scrollBy({ left: direction * track.clientWidth, behavior: 'smooth' });
    } catch (e) {
      track.scrollLeft += direction * track.clientWidth;
    }
  }

  function buildNav(track, config) {
    var nav = document.createElement('div');
    nav.className = config.navClass;
    nav.setAttribute('data-kron-picker-nav', '');
    nav.setAttribute('data-kron-nav-type', config.type);
    nav.innerHTML =
      '<button type="button" class="kron-picker-nav__btn" data-kron-picker-prev aria-label="Previous page">' + ICON_LEFT + '</button>' +
      '<span class="kron-picker-nav__counter" data-kron-picker-counter aria-live="polite">1 / 1</span>' +
      '<button type="button" class="kron-picker-nav__btn" data-kron-picker-next aria-label="Next page">' + ICON_RIGHT + '</button>';
    nav.querySelector('[data-kron-picker-prev]').addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      scrollByPage(track, -1);
    });
    nav.querySelector('[data-kron-picker-next]').addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation();
      scrollByPage(track, 1);
    });
    return nav;
  }

  function wireScroll(track, nav, config) {
    if (wiredScroll.has(track)) return;
    wiredScroll.add(track);
    var rafId = 0;
    track.addEventListener('scroll', function () {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        updateNav(nav, track, config);
        rafId = 0;
      });
    }, { passive: true });
  }
  function wireContent(track, nav, config) {
    if (wiredContent.has(track) || !window.MutationObserver) return;
    wiredContent.add(track);
    new MutationObserver(function () { updateNav(nav, track, config); }).observe(track, { childList: true });
  }
  function wireResize(track, nav, config) {
    if (wiredResize.has(track)) return;
    wiredResize.add(track);
    if (window.ResizeObserver) {
      new ResizeObserver(function () { updateNav(nav, track, config); }).observe(track);
    }
  }

  function setupTrack(track, config) {
    if (!track || !track.parentNode) return;
    var insertAfter = config.findInsertAfter(track);
    if (!insertAfter || !insertAfter.parentNode) return;
    var container = insertAfter.parentNode;
    var nav = navByTrack.get(track);
    if (!nav || !nav.isConnected) {
      var stale = container.querySelector(':scope > [data-kron-picker-nav][data-kron-nav-type="' + config.type + '"]');
      if (stale) stale.remove();
      nav = buildNav(track, config);
      navByTrack.set(track, nav);
      if (insertAfter.nextSibling) {
        container.insertBefore(nav, insertAfter.nextSibling);
      } else {
        container.appendChild(nav);
      }
    }
    wireScroll(track, nav, config);
    wireContent(track, nav, config);
    wireResize(track, nav, config);
    updateNav(nav, track, config);
  }

  function scanAll() {
    try {
      for (var i = 0; i < TRACK_CONFIGS.length; i++) {
        var config = TRACK_CONFIGS[i];
        var tracks = document.querySelectorAll(config.trackSelector);
        for (var j = 0; j < tracks.length; j++) {
          setupTrack(tracks[j], config);
        }
      }
    } catch (e) {}
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================
  function boot() {
    refreshComparePrices();
    scanAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('variant:change', refreshComparePrices);
  document.addEventListener('product:variant-change', refreshComparePrices);

  // MutationObserver: scanAll só (compare prices NUNCA chamado por aqui pra evitar wipe loop;
  // compare prices só atualiza em variant:change events que são triggered explicitamente).
  if (window.MutationObserver) {
    var rescanTimer = 0;
    var schedule = function () {
      if (rescanTimer) clearTimeout(rescanTimer);
      rescanTimer = setTimeout(function () {
        rescanTimer = 0;
        scanAll();
      }, 250);
    };
    // Kron 2026-05-23: scoped MO em containers específicos, NÃO no body inteiro
    var moTargets = [];
    function attachScopedObservers() {
      var targetSels = ['.bundle-offer', 'bundle-offer', '.kron-tpr__reviews', '.kron-tp__reviews'];
      for (var i = 0; i < targetSels.length; i++) {
        var nodes = document.querySelectorAll(targetSels[i]);
        for (var j = 0; j < nodes.length; j++) {
          if (moTargets.indexOf(nodes[j]) !== -1) continue;
          moTargets.push(nodes[j]);
          new MutationObserver(schedule).observe(nodes[j], { childList: true, subtree: false });
        }
      }
    }
    attachScopedObservers();
    document.addEventListener('shopify:section:load', attachScopedObservers);
  }

  /* Kron 2026-05-24: re-init nav on bundle picker render */
  document.addEventListener('kron:bundle-picker-rendered', function () {
    setTimeout(function () { if (typeof scanAll === 'function') scanAll(); }, 30);
  });
  window.addEventListener('resize', function () {
    setTimeout(function () { if (typeof scanAll === 'function') scanAll(); }, 100);
  });
})();



/* ===== assets/kron-variant-stock-v8.js ===== */
// Kron — variant stock v8 (sibling-label detection + fetch /products/H.js)
(function () {
  var productCache = null;
  var fetchPromise = null;

  function hashStock(variantId) {
    var day = Math.floor(Date.now() / 86400000);
    var n = parseInt(String(variantId || '').slice(-6).replace(/\D/g, '') || '0', 10);
    return ((n + day) % 3) + 1;
  }
  function getProductHandle() {
    var el = document.querySelector('[data-kron-variant-stock]');
    if (el && el.getAttribute('data-product-handle')) return el.getAttribute('data-product-handle');
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }
  function getCurrentVariantId() {
    var params = new URLSearchParams(location.search);
    var id = params.get('variant');
    if (id) return id;
    var formInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (formInput && formInput.value) return formInput.value;
    var el = document.querySelector('[data-kron-variant-stock]');
    return el ? el.getAttribute('data-variant-id') : null;
  }
  function fetchProduct() {
    if (productCache) return Promise.resolve(productCache);
    if (fetchPromise) return fetchPromise;
    var handle = getProductHandle();
    if (!handle) return Promise.resolve(null);
    fetchPromise = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.variants) {
          var map = {};
          for (var i = 0; i < data.variants.length; i++) {
            var v = data.variants[i];
            map[String(v.id)] = { available: v.available };
          }
          productCache = map;
        }
        return productCache;
      })
      .catch(function () { return null; });
    return fetchPromise;
  }
  // Detecta se algum elemento "selecionado" está em estado disabled
  // CRÍTICO: tema renderiza <input><label> como SIBLINGS, não pai/filho
  function isCurrentSwatchDisabled() {
    var picker = document.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
    if (!picker) return false;
    // Estratégia 1: input:checked → procurar label[for=input.id] no MESMO picker
    var checked = picker.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      var inp = checked[i];
      if (!inp.id) continue;
      var label = picker.querySelector('label[for="' + CSS.escape(inp.id) + '"]');
      if (label && (label.classList.contains('is-disabled') || label.classList.contains('disabled') || label.classList.contains('sold-out'))) return true;
      // também checar swatch dentro do label
      if (label) {
        var swatch = label.querySelector('.color-swatch, .block-swatch, .thumbnail-swatch');
        if (swatch && swatch.classList.contains('is-disabled')) return true;
      }
    }
    // Estratégia 2: elementos com .is-selected ou aria-checked
    var sels = ['.is-selected', '[aria-checked="true"]', '.color-swatch--selected'];
    for (var k = 0; k < sels.length; k++) {
      var nodes = picker.querySelectorAll(sels[k]);
      for (var m = 0; m < nodes.length; m++) {
        var n = nodes[m];
        if (n.classList.contains('is-disabled') || n.classList.contains('disabled') || n.classList.contains('sold-out')) return true;
        var lbl = n.closest('label');
        if (lbl && (lbl.classList.contains('is-disabled') || lbl.classList.contains('sold-out'))) return true;
      }
    }
    return false;
  }
  function isUnavailable(currentVariant, variantId, map) {
    // PRIORITY 1: DOM disabled state (mais confiável quando user clica)
    if (isCurrentSwatchDisabled()) return true;
    // PRIORITY 2: cache de /products/H.js
    if (map && map[variantId]) {
      if (map[variantId].available === false) return true;
    }
    // PRIORITY 3: event payload
    if (currentVariant && currentVariant.available === false) return true;
    return false;
  }
  function applyRender(currentVariant, map) {
    var els = document.querySelectorAll('[data-kron-variant-stock]');
    if (!els.length) return;
    var variantId = (currentVariant && currentVariant.id) ? String(currentVariant.id) : getCurrentVariantId();
    if (!variantId) return;
    var soldOut = isUnavailable(currentVariant, variantId, map);
    var stockCount = hashStock(variantId);
    var labelText = soldOut ? 'Sold out' : (stockCount === 1 ? 'Last one' : ('Only ' + stockCount + ' left'));
    els.forEach(function (el) {
      var currentLbl = el.querySelector('.kron-variant-stock__label');
      if (currentLbl && currentLbl.textContent === labelText && el.classList.contains('kron-variant-stock--sold-out') === soldOut) {
        el.removeAttribute('hidden');
        return;
      }
      if (!el.firstElementChild) {
        el.innerHTML = '<span class="kron-variant-stock__pulse"></span><span class="kron-variant-stock__label">' + labelText + '</span>';
      } else if (currentLbl) {
        currentLbl.textContent = labelText;
      }
      el.classList.toggle('kron-variant-stock--sold-out', soldOut);
      el.removeAttribute('hidden');
    });
  }
  function renderNow(currentVariant) {
    if (productCache) {
      applyRender(currentVariant, productCache);
    } else {
      applyRender(currentVariant, null);
      fetchProduct().then(function (map) { if (map) applyRender(currentVariant, map); });
    }
  }
  var pendingTimer = 0;
  var pendingVariant = null;
  function requestRender(v) {
    if (v) pendingVariant = v;
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () {
      pendingTimer = 0;
      var pv = pendingVariant;
      pendingVariant = null;
      renderNow(pv);
    }, 120);
  }
  function boot() {
    fetchProduct().then(function () { renderNow(); });
    renderNow();
    var picker = document.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
    if (picker) picker.addEventListener('click', function () { requestRender(); }, true);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('variant:change', function (e) {
    var v = e && e.detail && e.detail.variant;
    requestRender(v);
  });
  document.addEventListener('shopify:section:load', function () {
    productCache = null; fetchPromise = null;
    fetchProduct().then(function () { renderNow(); });
  });
})();

